import {
  NonAcceptedPaymentPlan,
  type ReinstatementContactInformationQuery,
  ReinstatementPaymentScheduleInput,
} from '../generated/hooks'

export type ReinstatementPlanName = 'Annually' | 'Semi-Annually' | 'Quarterly'

// Maps middleware's NonAcceptedPaymentPlan enum to the internal plan names rendered
// in the reinstatement UI. Values that don't correspond to one of our three plans
// (MONTHLY, EMPLOYEE, SCHOLARSHIP, COMPLIMENTARY, EMPLOY_SPOUSE) are dropped.
export const mapNonAcceptedPlans = (raw: NonAcceptedPaymentPlan[] | null | undefined): ReinstatementPlanName[] => {
  if (!raw) return []
  const mapped: ReinstatementPlanName[] = []
  for (const value of raw) {
    if (value === NonAcceptedPaymentPlan.Quarterly) mapped.push('Quarterly')
    else if (value === NonAcceptedPaymentPlan.SemiAnnual) mapped.push('Semi-Annually')
    else if (value === NonAcceptedPaymentPlan.FullPay) mapped.push('Annually')
  }
  return mapped
}

// Maps the UI plan name to the typed `ReinstatementPaymentScheduleInput` enum
// expected on the reinstateMember mutation. Middleware (2026-05-26) made `scheduleId`
// a typed enum at the schema layer rather than a free-form String, so we use the
// generated enum directly — runtime values serialize to FULL_PAY / SEMI_ANNUAL /
// QUARTERLY on the wire.
const PLAN_NAME_TO_SCHEDULE_ID: Record<ReinstatementPlanName, ReinstatementPaymentScheduleInput> = {
  Annually: ReinstatementPaymentScheduleInput.FullPay,
  'Semi-Annually': ReinstatementPaymentScheduleInput.SemiAnnual,
  Quarterly: ReinstatementPaymentScheduleInput.Quarterly,
}

export const mapPlanNameToScheduleId = (name: ReinstatementPlanName): ReinstatementPaymentScheduleInput =>
  PLAN_NAME_TO_SCHEDULE_ID[name]

export type ReinstatementContactInfo = {
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  physicianName: string | null
  msrpAmount: number | null
  nonAcceptedPaymentPlans: NonAcceptedPaymentPlan[] | null
}

export type ContactInfoOutcome =
  | { kind: 'success'; data: ReinstatementContactInfo }
  | { kind: 'not-found'; message: string }
  | { kind: 'network'; error: unknown }

type RawResult = ReinstatementContactInformationQuery['reinstatementContactInformation'] | undefined

export const mapContactInfoQueryResult = (raw: RawResult): ContactInfoOutcome => {
  if (raw?.__typename === 'ReinstatementContactInformation') {
    return {
      kind: 'success',
      data: {
        firstName: raw.FirstName ?? null,
        lastName: raw.LastName ?? null,
        email: raw.Email ?? null,
        phone: raw.MobilePhone ?? null,
        physicianName: raw.Account_Name ?? null,
        msrpAmount: raw.MSRP_Amount__c ?? null,
        nonAcceptedPaymentPlans: raw.Account_Non_Accepted_Payment_Plans__c ?? null,
      },
    }
  }

  if (raw?.__typename === 'ReinstatementContactInformationFailure') {
    const notFound = raw.errors.find((e) => e.__typename === 'ReinstatementContactInformationNotFoundError')
    return { kind: 'not-found', message: notFound?.message ?? 'Contact information not found.' }
  }

  return { kind: 'network', error: new Error('Unexpected reinstatementContactInformation response shape') }
}
